const SUBDIVISION_KEY = 'dallas-subdivision-blacklist';
const TOWNSHIP_KEY = 'dallas-township-blacklist';

// --- Subdivision ---
export function getBlacklistedSubdivisions(): string[] {
  try {
    const stored = localStorage.getItem(SUBDIVISION_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

export function saveBlacklistedSubdivisions(list: string[]): void {
  localStorage.setItem(SUBDIVISION_KEY, JSON.stringify(list));
}

export function addSubdivisionToBlacklist(subdivision: string): void {
  const list = getBlacklistedSubdivisions();
  const normalized = subdivision.toUpperCase().trim();
  if (normalized && !list.includes(normalized)) {
    list.push(normalized);
    saveBlacklistedSubdivisions(list);
  }
}

export function removeSubdivisionFromBlacklist(subdivision: string): void {
  const list = getBlacklistedSubdivisions();
  saveBlacklistedSubdivisions(list.filter(s => s !== subdivision));
}

export function isSubdivisionBlacklisted(subdivision: string): boolean {
  if (!subdivision) return false;
  return getBlacklistedSubdivisions().includes(subdivision.toUpperCase().trim());
}

// --- Township / City ---
export function getBlacklistedTownships(): string[] {
  try {
    const stored = localStorage.getItem(TOWNSHIP_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

export function saveBlacklistedTownships(list: string[]): void {
  localStorage.setItem(TOWNSHIP_KEY, JSON.stringify(list));
}

export function addTownshipToBlacklist(township: string): void {
  const list = getBlacklistedTownships();
  const normalized = township.toUpperCase().trim();
  if (normalized && !list.includes(normalized)) {
    list.push(normalized);
    saveBlacklistedTownships(list);
  }
}

export function removeTownshipFromBlacklist(township: string): void {
  const list = getBlacklistedTownships();
  saveBlacklistedTownships(list.filter(s => s !== township));
}

export function isTownshipBlacklisted(township: string): boolean {
  if (!township) return false;
  return getBlacklistedTownships().includes(township.toUpperCase().trim());
}

// --- Extraction ---

/**
 * Extract subdivision name from a Dallas County legal description.
 */
export function extractSubdivision(legalDescription: string): string | null {
  if (!legalDescription) return null;
  const nameMatch = legalDescription.match(/Name:\s*([A-Z0-9\s\-'&.#/]+?)(?:\s+(?:Lot|Block|Unit|Phase|Section|Tract|Addition|Sec|Vol|Pg|Page|Abstract|Survey|Acres?|Part)\b|$)/i);
  if (nameMatch) {
    return nameMatch[1].toUpperCase().trim();
  }
  return null;
}

/**
 * Extract township/city from a Dallas County legal description.
 * Handles: "Township: DALLAS", "Township: IRVING", etc.
 */
export function extractTownship(legalDescription: string): string | null {
  if (!legalDescription) return null;
  const match = legalDescription.match(/Township:\s*([A-Z\s\-'&.]+?)(?:\s+(?:Reference|Ref|Lot|Block|$))/i);
  if (match) {
    return match[1].toUpperCase().trim();
  }
  // Fallback: Township at end of string
  const fallback = legalDescription.match(/Township:\s*([A-Z\s\-'&.]+)/i);
  if (fallback) {
    return fallback[1].toUpperCase().trim();
  }
  return null;
}

/**
 * Check if a record should be blacklisted based on subdivision OR township.
 */
export function isRecordBlacklisted(legalDescription: string): { blacklisted: boolean; reason: string | null } {
  const subdivision = extractSubdivision(legalDescription);
  const township = extractTownship(legalDescription);

  if (township && isTownshipBlacklisted(township)) {
    return { blacklisted: true, reason: `Blacklisted township: ${township}` };
  }
  if (subdivision && isSubdivisionBlacklisted(subdivision)) {
    return { blacklisted: true, reason: `Blacklisted subdivision: ${subdivision}` };
  }
  return { blacklisted: false, reason: null };
}

// Legacy aliases
export const addToBlacklist = addSubdivisionToBlacklist;
export const removeFromBlacklist = removeSubdivisionFromBlacklist;
